import { formatPhoneNumber, PhoneNumberE164, toPhoneNumberE164 } from "@sp-crm/core";
import { usePreventUnload } from "hooks/use-prevent-unload";
import React, { Reducer } from "react";
import { useAutoComplete } from "store/selectors/hooks";
import { Spinner } from "./spinner";

const inlineInputClasses =
    "form-input block w-full rounded-sm border-gray-300 md:rounded " +
    "hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300";

const inlineLabelClasses = "mb-1 block";

interface ComponentState {
    upstreamValue: string;
    mode: "pristine" | "dirty" | "pending";
    currentValue: string;
}

type Action =
    | { type: "change"; value: string }
    | { type: "blur"; value: string }
    | { type: "updateUpstream"; value: string };

const invalidAction = (action: Action): never => {
    throw new Error(`unknown action type ${action.type}`);
};
const invalidMode = (mode: ComponentState["mode"]): never => {
    throw new Error(`unknown mode ${mode}`);
};

const reducer: Reducer<ComponentState, Action> = (p, a): ComponentState => {
    switch (a.type) {
        case "change":
            return { ...p, mode: "dirty", currentValue: a.value };
        case "blur":
            return {
                ...p,
                mode: p.upstreamValue === a.value ? "pristine" : "pending",
                currentValue: a.value,
            };
        case "updateUpstream":
            switch (p.mode) {
                case "dirty":
                    return { ...p, upstreamValue: a.value };
                case "pending":
                    return {
                        ...p,
                        mode: p.currentValue === a.value ? "pristine" : "pending",
                        upstreamValue: a.value,
                    };
                case "pristine":
                    return { ...p, currentValue: a.value, upstreamValue: a.value };
            }
            return invalidMode(p.mode);
    }
    return invalidAction(a);
};

interface InlineInputShellProps {
    label: React.ReactNode;
    pending: boolean;
    children: React.ReactNode;
}

const InlineInputShell: React.FC<InlineInputShellProps> = props => {
    const { label, pending, children } = props;

    return (
        <label className="block">
            <span className={inlineLabelClasses}>{label}</span>
            <div className="relative">
                {children}
                {pending ? (
                    <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                        <Spinner />
                    </div>
                ) : null}
            </div>
        </label>
    );
};

interface InlineTypedFieldProps {
    label: React.ReactNode;
    type: string;
    initial: string;
    onCommit: (newValue: string) => void;
}

export const InlineTypedField: React.FC<InlineTypedFieldProps> = props => {
    const { label, type, initial, onCommit } = props;

    const [state, dispatch] = React.useReducer(reducer, {
        upstreamValue: initial,
        currentValue: initial,
        mode: "pristine",
    });
    const autoComplete = useAutoComplete();
    const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({ type: "change", value: e.target.value });
        },
        [dispatch],
    );
    const flushDirtyValue = React.useCallback(() => {
        if (state.mode === "dirty") {
            const commitValue = (state.currentValue ?? "").trim();
            dispatch({ type: "blur", value: commitValue });
            onCommit(commitValue);
        }
    }, [dispatch, state.currentValue, state.mode, onCommit]);
    usePreventUnload(state.mode === "dirty", flushDirtyValue);
    React.useEffect(() => {
        dispatch({ type: "updateUpstream", value: initial });
    }, [dispatch, initial]);

    return (
        <InlineInputShell label={label} pending={state.mode === "pending"}>
            <input
                type={type}
                value={state.currentValue ?? ""}
                onChange={handleChange}
                onBlur={flushDirtyValue}
                autoComplete={autoComplete}
                className={inlineInputClasses}
            />
        </InlineInputShell>
    );
};

interface InlineTextFieldProps {
    label: React.ReactNode;
    initial: string;
    onCommit: (newValue: string) => void;
}

export const InlineTextField: React.FC<InlineTextFieldProps> = props => (
    <InlineTypedField
        label={props.label}
        type="text"
        initial={props.initial}
        onCommit={props.onCommit}
    />
);

interface InlineTextAreaFieldProps {
    label: React.ReactNode;
    initial: string;
    rows: number;
    onCommit: (newValue: string) => void;
}

export const InlineTextAreaField: React.FC<InlineTextAreaFieldProps> = props => {
    const { label, initial, rows, onCommit } = props;

    const [state, dispatch] = React.useReducer(reducer, {
        upstreamValue: initial,
        currentValue: initial,
        mode: "pristine",
    });
    const autoComplete = useAutoComplete();
    const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            dispatch({ type: "change", value: e.target.value });
        },
        [dispatch],
    );
    const flushDirtyValue = React.useCallback(() => {
        if (state.mode === "dirty") {
            const commitValue = (state.currentValue ?? "").trim();
            dispatch({ type: "blur", value: commitValue });
            onCommit(commitValue);
        }
    }, [dispatch, state.currentValue, state.mode, onCommit]);
    usePreventUnload(state.mode === "dirty", flushDirtyValue);
    React.useEffect(() => {
        dispatch({ type: "updateUpstream", value: initial });
    }, [dispatch, initial]);

    return (
        <InlineInputShell label={label} pending={state.mode === "pending"}>
            <textarea
                value={state.currentValue ?? ""}
                rows={rows}
                onChange={handleChange}
                onBlur={flushDirtyValue}
                autoComplete={autoComplete}
                className={`${inlineInputClasses} resize-y`}
            />
        </InlineInputShell>
    );
};

interface InlinePhoneFieldProps {
    label: React.ReactNode;
    initial: PhoneNumberE164 | null;
    onCommit: (newValue: PhoneNumberE164) => void;
}

export const InlinePhoneField: React.FC<InlinePhoneFieldProps> = props => {
    const { label, initial, onCommit } = props;

    const initialValue = initial || "";
    const [state, dispatch] = React.useReducer(reducer, {
        upstreamValue: initialValue,
        currentValue: initialValue,
        mode: initialValue === toPhoneNumberE164(initialValue) ? "pristine" : "dirty",
    });
    const autoComplete = useAutoComplete();
    const inputValue = formatPhoneNumber(state.currentValue);
    const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({ type: "change", value: e.target.value });
        },
        [dispatch],
    );
    const flushDirtyValue = React.useCallback(() => {
        if (state.mode === "dirty") {
            const toCommit = toPhoneNumberE164(inputValue);
            dispatch({ type: "blur", value: toCommit });
            onCommit(toCommit);
        }
    }, [dispatch, inputValue, state.mode, onCommit]);
    usePreventUnload(state.mode === "dirty", flushDirtyValue);
    React.useEffect(() => {
        dispatch({ type: "updateUpstream", value: initialValue });
    }, [dispatch, initialValue]);

    return (
        <InlineInputShell label={label} pending={state.mode === "pending"}>
            <input
                value={inputValue}
                onChange={handleChange}
                onBlur={flushDirtyValue}
                type="tel"
                autoComplete={autoComplete}
                className={inlineInputClasses}
            />
        </InlineInputShell>
    );
};

interface InlineSelectFieldProps {
    label: React.ReactNode;
    value: string;
    onCommit: (newValue: string) => void;
    children: React.ReactNode;
}

export const InlineSelectField: React.FC<InlineSelectFieldProps> = props => {
    const { label, value, onCommit, children } = props;

    const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            onCommit(e.target.value);
        },
        [onCommit],
    );

    return (
        <label className="block">
            <span className={inlineLabelClasses}>{label}</span>
            <select
                value={value}
                onChange={handleChange}
                className="form-select block w-full rounded-sm border-gray-300 hover:border-brand-600 focus:ring-2 focus:ring-brand-300 md:rounded">
                {children}
            </select>
        </label>
    );
};
